import React, { createContext, useCallback, useState } from 'react';

import Loading from '@justgiving/loading';

import Modal from '../components/Modal/Modal';
import Portal from '../components/Portal/Portal';
import { Toast, ToastProps } from '../components/Toast/Toast';

import styles from '../components/Loading/Loading.scss';

export type AppContext = {
  showToast(toast: ToastProps): void;
  hideToast(): void;
  startLoading(): void;
  stopLoading(): void;
  isSSR: boolean;
  hideModal(): void;
  showModal({ modal, overwrite }: { modal: MODAL; overwrite?: boolean }): void;
  openModal: MODAL | null;
  checkoutHref: string;
  showCheckoutModal: (href: string) => void;
  showCheckoutPopup: (
    href: string,
    popupWidth: number,
    popupHeight: number,
  ) => void;
  popupWindow: Window | null;
};

export const AppContext = createContext<AppContext | undefined>(undefined);
AppContext.displayName = 'AppContext';

export const AppContextProvider = AppContext.Provider;

export enum MODAL {
  ENCOURAGE_DONATE,
  ENCOURAGE_SHARE,
  SHARE,
  DONATE_TO_YOURSELF,
  CHECKOUT,
  POPUP_BLOCKED,
  POPUP_REFOCUS,
  INSTAGRAM_SHARE,
  SNAPCHAT_SHARE,
}

export const AppProvider: React.FunctionComponent = ({ children }) => {
  const [toast, setToast] = useState<ToastProps | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<MODAL | null>(null);
  const [checkoutHref, setCheckoutHref] = useState('');
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  const showToast = useCallback((toast: ToastProps) => {
    setToast(toast);
  }, []);

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, []);

  const hideModal = useCallback(() => {
    setOpenModal(null);
  }, []);

  const showModal = useCallback(
    ({ modal, overwrite = false }: { modal: MODAL; overwrite?: boolean }) => {
      setOpenModal(prevModal => {
        if (overwrite) {
          return modal;
        }
        return prevModal !== null ? prevModal : modal;
      });
    },
    [],
  );

  const showCheckoutModal = useCallback(
    (href: string) => {
      setCheckoutHref(href);
      showModal({ modal: MODAL.CHECKOUT });
    },
    [showModal],
  );

  const showCheckoutPopup = useCallback(
    (href: string, popupWidth: number, popupHeight: number) => {
      setCheckoutHref(href);

      const screenX = window.screenX;
      const screenY = window.screenY;
      const screenWidth = window.screen.width;
      const screenHeight = window.screen.height;

      const popupLeft = screenX + (screenWidth / 2 - popupWidth / 2);
      const popupTop = screenY + (screenHeight / 2 - popupHeight / 2);
      const windowFeatures = `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop}`;

      const urlParams = new URLSearchParams(window.location.search);
      const popupCheckout =
        urlParams.get('popupBlocked') === 'true'
          ? null
          : window.open(href, '_blank', windowFeatures);

      if (popupCheckout) {
        setPopupWindow(popupCheckout);
        showModal({ modal: MODAL.POPUP_REFOCUS });
        popupCheckout?.focus();
      } else {
        showModal({ modal: MODAL.POPUP_BLOCKED });
      }
    },
    [showModal],
  );

  const isSSR = typeof window === 'undefined';

  return (
    <AppContextProvider
      value={{
        showToast,
        hideToast,
        startLoading,
        stopLoading,
        isSSR,
        hideModal,
        showModal,
        openModal,
        checkoutHref,
        showCheckoutModal,
        showCheckoutPopup,
        popupWindow,
      }}
    >
      {children}
      {isLoading ? (
        <Modal className={styles.spinner}>
          <Loading />
        </Modal>
      ) : null}
      {toast ? (
        <Portal>
          <Toast onDismiss={hideToast} {...toast} />
        </Portal>
      ) : null}
    </AppContextProvider>
  );
};

AppProvider.displayName = 'AppProvider';
